import { useEffect, useRef, useState } from 'react'

import type { Supply48DataSource } from '@ebalance/proto/bffuser/v1/supply_48_data_model_pb'

import { resolveScrollTargetLegId } from '~/pages/Supply48Data/models/scrollTarget'

/**
 * AC-S4D-052 の一時ハイライト表示時間 (ms)。
 *
 * 専用の design 指定は無い (design/supply/48data.html にも遷移ハイライト専用の
 * タイマー値は無く、識別子・スクロール方式・スタイルと同じく EN/Des 判断範囲)。
 * 担当者が横スクロール後に列を認識するのに十分でありながら、画面に長く
 * 居座って通常のグラフ対象列ハイライトと混同され続けない長さとして 3 秒を選ぶ。
 */
export const SCROLL_HIGHLIGHT_DURATION_MS = 3000

export type UseSupply48ScrollHighlightParams = {
  /**
   * 遷移パラメータの契約識別子 (取引ID、`supply48QueryParams.ts` の
   * `parseSupply48ScrollTarget` が解決する)。undefined なら何もしない
   * (通常遷移・直接アクセスとの互換)。
   */
  scrollTargetTradeId: string | undefined
  /**
   * 現在のフィルタ結果の列一覧 (`ListSupply48Data` の sources)。未取得 (取得中)
   * の間だけ undefined で、その間は判定せずデータが揃うのを待つ。取得済みの
   * 空結果 (0 件) と取得失敗は `[]` で渡し、「対象なし」として判定を確定させる。
   */
  legs: Supply48DataSource[] | undefined
}

export type UseSupply48ScrollHighlightResult = {
  /** 一時ハイライト中の legId (supply48SourceId)。対象なし・解除済みは null。 */
  highlightedLegId: string | null
}

/**
 * AC-S4D-052: 相対取引契約管理からの遷移直後、指定契約の列を一時ハイライトする
 * 対象を解決し、`SCROLL_HIGHLIGHT_DURATION_MS` 後に自動解除する。
 *
 * **呼び出しは遷移パラメータの保持主体であるページ (`Supply48Data/index.tsx`) から
 * 行う**。グリッド (`Supply48DataGrid`) は対象日・エリア・売買・区分を変えると
 * query key が変わって unmount → remount するため、一度きりの判定をグリッド側に
 * 置くと remount ごとに初期化され、遷移とは無関係なフィルタ操作で横スクロールが
 * 再発火する (ページは remount しないので、ここに置けば再マウントを跨いで一度きりが
 * 保証される)。
 *
 * 判定はデータが最初に揃った時点で 1 度だけ行い、以降フィルタ変更で legs が
 * 変わっても再判定しない (「遷移直後」の一度きりの挙動。AC-S4D-052 ※2: 対象が
 * 当初から存在しなければ以後も何もしない)。判定後にフィルタ結果が変わったら
 * 「遷移直後」は終わったものとしてハイライトを落とす。
 *
 * 実際の横スクロール DOM 操作・ハイライト描画は呼び出し側が `highlightedLegId`
 * を見て行う (本 hook は DOM に触れず、判定とタイマーの状態管理のみを担う)。
 */
export function useSupply48ScrollHighlight({
  scrollTargetTradeId,
  legs,
}: UseSupply48ScrollHighlightParams): UseSupply48ScrollHighlightResult {
  const triggeredRef = useRef(false)
  const [highlightedLegId, setHighlightedLegId] = useState<string | null>(null)

  // 対象列の判定 (ページの生存中 1 度きり)。判定後にフィルタ結果が入れ替わったら
  // 「遷移直後」は終わりなのでハイライトを落とす (グリッドが再マウントしても
  // scrollIntoView が二度走らない)。
  useEffect(() => {
    if (triggeredRef.current) {
      setHighlightedLegId(null)
      return
    }
    if (scrollTargetTradeId === undefined) return
    if (legs === undefined) return
    triggeredRef.current = true
    const legId = resolveScrollTargetLegId(legs, scrollTargetTradeId)
    if (legId !== undefined) setHighlightedLegId(legId)
  }, [scrollTargetTradeId, legs])

  // 一時ハイライトの自動解除。
  useEffect(() => {
    if (highlightedLegId === null) return
    const timer = setTimeout(() => {
      setHighlightedLegId(null)
    }, SCROLL_HIGHLIGHT_DURATION_MS)
    return () => clearTimeout(timer)
  }, [highlightedLegId])

  return { highlightedLegId }
}
